import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
	Building2,
	Calendar,
	DollarSign,
	Eye,
	FileText,
	Loader2,
	MoreHorizontal,
	Search,
} from "lucide-react";
import { useState } from "react";
import { DashboardLayout } from "@/components/layouts/DashboardLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { useTRPC } from "@/integrations/trpc/react";
import { useAuth } from "@/lib/auth";

export const Route = createFileRoute("/awards/")({
	component: AwardsPage,
});

function AwardsPage() {
	const { user } = useAuth();
	const trpc = useTRPC();
	const [search, setSearch] = useState("");

	// Get all solicitations first
	const { data: solicitations, isLoading: solicitationsLoading } = useQuery(
		trpc.solicitations.list.queryOptions({
			buyerOrgId: user?.organizationId ?? "",
			limit: 100,
			offset: 0,
			status: "awarded",
		}),
	);

	const isLoading = solicitationsLoading;

	// Filter awarded solicitations by search
	const filteredAwards = (solicitations ?? []).filter((sol) => {
		return sol.title.toLowerCase().includes(search.toLowerCase());
	});

	if (isLoading) {
		return (
			<DashboardLayout>
				<div className="flex items-center justify-center h-64">
					<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
				</div>
			</DashboardLayout>
		);
	}

	return (
		<DashboardLayout>
			<div className="space-y-6">
				<div>
					<h1 className="text-3xl font-bold tracking-tight">Contracts</h1>
					<p className="text-muted-foreground">
						Manage contract awards and agreements
					</p>
				</div>

				{/* Stats */}
				<div className="grid gap-4 md:grid-cols-4">
					<Card>
						<CardContent className="pt-6">
							<div className="flex items-center gap-2">
								<FileText className="h-4 w-4 text-muted-foreground" />
								<span className="text-sm text-muted-foreground">
									Awarded Solicitations
								</span>
							</div>
							<p className="text-2xl font-bold mt-1">
								{(solicitations ?? []).length}
							</p>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="pt-6">
							<div className="flex items-center gap-2">
								<DollarSign className="h-4 w-4 text-muted-foreground" />
								<span className="text-sm text-muted-foreground">
									Total Contracts
								</span>
							</div>
							<p className="text-2xl font-bold mt-1">
								{(solicitations ?? []).length}
							</p>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="pt-6">
							<div className="flex items-center gap-2">
								<Calendar className="h-4 w-4 text-muted-foreground" />
								<span className="text-sm text-muted-foreground">
									This Year
								</span>
							</div>
							<p className="text-2xl font-bold mt-1">
								{
									(solicitations ?? []).filter((s) => {
										const year = new Date(s.updated_at).getFullYear();
										return year === new Date().getFullYear();
									}).length
								}
							</p>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="pt-6">
							<div className="flex items-center gap-2">
								<Building2 className="h-4 w-4 text-muted-foreground" />
								<span className="text-sm text-muted-foreground">
									Public Awards
								</span>
							</div>
							<p className="text-2xl font-bold mt-1">
								{(solicitations ?? []).filter((s) => s.is_public).length}
							</p>
						</CardContent>
					</Card>
				</div>

				{/* Filters */}
				<div className="flex flex-col sm:flex-row gap-4">
					<div className="relative flex-1">
						<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
						<Input
							placeholder="Search awards..."
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							className="pl-9"
						/>
					</div>
				</div>

				{/* Table */}
				<div className="border rounded-lg">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Solicitation</TableHead>
								<TableHead>Type</TableHead>
								<TableHead>Updated</TableHead>
								<TableHead>Public</TableHead>
								<TableHead className="text-right">Actions</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{filteredAwards.map((award) => (
								<TableRow key={award.id}>
									<TableCell>
										<Link
											to="/solicitations/$id"
											params={{ id: award.id }}
											className="font-medium hover:underline"
										>
											{award.title}
										</Link>
									</TableCell>
									<TableCell>
										<Badge variant="outline">{award.type}</Badge>
									</TableCell>
									<TableCell>
										{new Date(award.updated_at).toLocaleDateString()}
									</TableCell>
									<TableCell>
										<Badge
											variant={award.is_public ? "default" : "secondary"}
										>
											{award.is_public ? "Public" : "Private"}
										</Badge>
									</TableCell>
									<TableCell className="text-right">
										<DropdownMenu>
											<DropdownMenuTrigger asChild>
												<Button variant="ghost" size="icon">
													<MoreHorizontal className="h-4 w-4" data-lucide="more-horizontal" />
												</Button>
											</DropdownMenuTrigger>
											<DropdownMenuContent align="end">
												<DropdownMenuItem asChild>
													<Link
														to="/solicitations/$id"
														params={{ id: award.id }}
													>
														<Eye className="mr-2 h-4 w-4" />
														View Details
													</Link>
												</DropdownMenuItem>
											</DropdownMenuContent>
										</DropdownMenu>
									</TableCell>
								</TableRow>
							))}
							{filteredAwards.length === 0 && (
								<TableRow>
									<TableCell
										colSpan={5}
										className="text-center py-8 text-muted-foreground"
									>
										No awards found
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</div>
			</div>
		</DashboardLayout>
	);
}
